import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Table,
  Input,
  Label,
  ButtonGroup,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import axios from "axios";
import qrcode from "qrcode-js";
import JsBarcode from "jsbarcode";
import { v4 as uuidv4 } from "uuid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { generateEAN13, generateItemReference } from "../../../../utils/ean13Generator";

function GeneradorEtiquetasChina() {
  const { user, userType } = useContext(AuthContext);
  
  // URLs de API
  const URL_COLORES = import.meta.env.VITE_URL_COLORES;
  const URL_LOGS = import.meta.env.VITE_URL_LOGS;
  const URL_TALLAS = import.meta.env.VITE_URL_TALLAS;
  const URL_PRE_ARTICULOS = import.meta.env.VITE_URL_PRE_ARTICULOS;
  const URL_ARTICULOS_PROPORCION = import.meta.env.VITE_URL_ARTICULOS_PROPORCION;

  // Estados principales
  const [allTallas, setAllTallas] = useState([]);
  const [allColores, setAllColores] = useState([]);
  const [customSizeOrder, setCustomSizeOrder] = useState([]);

  // Estados para nuevo artículo
  const [nuevoCodigo, setNuevoCodigo] = useState("");
  const [selectedColores, setSelectedColores] = useState([]);
  const [selectedTallas, setSelectedTallas] = useState([]);

  // Estados para la tabla de proporción
  const [arrayColores, setArrayColores] = useState([]);
  const [arrayTallas, setArrayTallas] = useState([]);
  const [cantidades, setCantidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [validaBoton, setValidaBoton] = useState(true);
  // Guard síncrono contra doble-submit / reintentos (evita PreArticulos duplicados).
  const submittingRef = useRef(false);




  // Cargar tallas y colores al montar el componente
  useEffect(() => {
    cargarTallasYColores();
  }, []);

  // Actualizar tabla automáticamente cuando cambien colores, tallas o modo RFID
  useEffect(() => {
    if (mostrarTabla && nuevoCodigo.trim() && selectedColores.length > 0 && selectedTallas.length > 0) {
      actualizarTabla();
    } else if (mostrarTabla && (selectedColores.length === 0 || selectedTallas.length === 0)) {
      setMostrarTabla(false);
    }
  }, [selectedColores, selectedTallas]);

  const cargarTallasYColores = async () => {
    try {
      // Cargar tallas
      const tallasResponse = await axios.get(URL_TALLAS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      });
      
      const tallasData = tallasResponse.data;
      setAllTallas(tallasData);
      
      const customeSizeOrder = tallasData.map((t) => t.name);
      setCustomSizeOrder(customeSizeOrder);

      // Cargar colores
      const coloresResponse = await axios.get(URL_COLORES, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      });
      
      setAllColores(coloresResponse.data);
      
    } catch (error) {
      console.error("Error cargando tallas y colores:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Could not load sizes and colors",
      });
    }
  };

  const actualizarTabla = () => {
    // Filtrar y ordenar colores seleccionados
    const coloresArray = allColores
      .filter(color => selectedColores.includes(color._id))
      .sort((a, b) => a.name.localeCompare(b.name));

    setArrayColores(coloresArray);

    // Filtrar y ordenar tallas seleccionadas
    const tallasArray = allTallas
      .filter(talla => selectedTallas.includes(talla._id))
      .sort((a, b) => {
        const indexA = customSizeOrder.indexOf(a.name);
        const indexB = customSizeOrder.indexOf(b.name);

        if (indexA === -1 && indexB === -1) return a.name.localeCompare(b.name);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });

    setArrayTallas(tallasArray);

    // Conservar cantidades existentes y agregar nuevas combinaciones en 0
    const nuevasCantidades = [];

    coloresArray.forEach(color => {
      tallasArray.forEach(talla => {
        // Buscar si ya existe esta combinación
        const existingItem = cantidades.find(
          item => item.colorId === color._id && item.tallaId === talla._id
        );

        const itemRef = generateItemReference(nuevoCodigo, color.name, talla.name);
        const ean13Code = generateEAN13(itemRef);

        nuevasCantidades.push({
          id: existingItem?.id || uuidv4(),
          colorId: color._id,
          colorName: color.name,
          tallaId: talla._id,
          tallaNombre: talla.name,
          cantidad: existingItem?.cantidad || 0,
          rfid: ean13Code,
        });
      });
    });

    setCantidades(nuevasCantidades);
  };

  const generarTabla = () => {
    if (!nuevoCodigo.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Code required",
        text: "Please enter an article code",
      });
      return;
    }

    if (selectedColores.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Colors required",
        text: "Please select at least one color",
      });
      return;
    }

    if (selectedTallas.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Sizes required",
        text: "Please select at least one size",
      });
      return;
    }

    actualizarTabla();
    setMostrarTabla(true);
  };

  const handleCantidadChange = (colorId, tallaId, value) => {
    const updatedCantidades = cantidades.map(item => {
      if (item.colorId === colorId && item.tallaId === tallaId) {
        return { ...item, cantidad: parseInt(value) || 0 };
      }
      return item;
    });
    setCantidades(updatedCantidades);
  };

const generarEtiquetasPDF = async (rfidMap = {}) => {
  try {
    const cantidadesValidas = cantidades.filter(item => item.cantidad > 0);

    if (cantidadesValidas.length === 0) {
      Swal.fire({ icon: "warning", title: "No quantities", text: "Please enter at least one quantity greater than 0" });
      return;
    }

    setLoading(true);
    const labelWidth = 35;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [labelWidth, 65],
      compress: false,
      precision: 16,
    });

    let isFirstPage = true;

    for (const item of cantidadesValidas) {
      const color = allColores.find(c => c._id === item.colorId);
      const talla = allTallas.find(t => t._id === item.tallaId);
      const colorPrefijo = color?.prefijo || "";
      const colorNombre = color?.name || "";
      const tallaNombre = talla?.name || "";
      // OJO: son DOS codigos distintos y NO deben fusionarse en una sola
      // variable. El CODE128 de china tiene que leer el estilo general
      // (HS1236); el QR si lleva color y talla (HS1236NBS). Al reescribir este
      // bloque en el commit ab437a6 se unificaron y toda la produccion salio
      // con el codigo de variante en el codigo de barras.
      const codigoGeneral = nuevoCodigo;
      const codigoVariante = `${nuevoCodigo}${colorPrefijo}${tallaNombre}`;
      // Usa el RFID del mapa pasado directamente (evita stale closure), luego state, luego hash
      const rfid = rfidMap[`${item.colorId}_${item.tallaId}`] || item.rfid || generateEAN13(generateItemReference(nuevoCodigo, colorNombre, tallaNombre));

      for (let i = 0; i < item.cantidad; i++) {
        if (!isFirstPage) doc.addPage();
        isFirstPage = false;

        let y = 2;

        // RFID label
        doc.setFontSize(6).setFont(undefined, "normal");
        const rl = "RFID";
        doc.text(rl, (labelWidth - doc.getTextWidth(rl)) / 2, y + 3);
        y += 6;

        // SIZE + STYLE
        doc.setFontSize(7).setFont(undefined, "bold");
        doc.text(`SIZE: ${tallaNombre}`, 2, y); y += 3.5;
        doc.text(`STYLE: ${nuevoCodigo}`, 2, y); y += 4;

        // Code 128 barcode (EAN)
        try {
          const c128 = document.createElement("canvas");
          JsBarcode(c128, codigoGeneral, { format: "CODE128", width: 2, height: 50, displayValue: false, margin: 0, background: "#FFFFFF", lineColor: "#000000" });
          doc.addImage(c128.toDataURL("image/png", 1.0), "PNG", 2, y, 31, 10);
          y += 11;
        } catch (e) { y += 11; }

        // QR Code
        try {
          const qrDataUrl = await qrcode.toDataURL(codigoVariante);
          const qrSize = 16;
          doc.addImage(qrDataUrl, "PNG", (labelWidth - qrSize) / 2, y, qrSize, qrSize);
          y += qrSize + 1;
          doc.setFontSize(6).setFont(undefined, "bold");
          doc.text(codigoVariante, (labelWidth - doc.getTextWidth(codigoVariante)) / 2, y);
          y += 4;
        } catch (e) { y += 20; }

        // EAN-13 barcode (RFID)
        try {
          const ean13 = document.createElement("canvas");
          JsBarcode(ean13, rfid, { format: "EAN13", width: 2, height: 50, displayValue: false, margin: 0, background: "#FFFFFF", lineColor: "#000000" });
          doc.addImage(ean13.toDataURL("image/png", 1.0), "PNG", 2, y, 31, 10);
          y += 11;
          doc.setFontSize(6).setFont(undefined, "normal");
          doc.text(rfid, (labelWidth - doc.getTextWidth(rfid)) / 2, y);
        } catch (e) {}
      }
    }

    await axios.post(URL_LOGS, {
      tipo: "Generar Etiquetas China",
      detalle: `${nuevoCodigo} - Total: ${cantidadesValidas.reduce((sum, item) => sum + item.cantidad, 0)} etiquetas`,
      user: user.id,
    }, { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

    window.open(URL.createObjectURL(doc.output("blob", { compress: false })), "_blank");

    Swal.fire({ icon: "success", title: "Labels generated!", text: `${cantidadesValidas.reduce((sum, item) => sum + item.cantidad, 0)} labels were generated`, timer: 2000 });

    setNuevoCodigo("");
    setSelectedColores([]);
    setSelectedTallas([]);
    setMostrarTabla(false);

  } catch (error) {
    console.error("Error generando etiquetas:", error);
    Swal.fire({ icon: "error", title: "Error", text: "Could not generate labels" });
  } finally {
    setLoading(false);
  }
};

  const limpiarCantidades = () => {
    const cantidadesLimpias = cantidades.map(item => ({ ...item, cantidad: 0 }));
    setCantidades(cantidadesLimpias);
  };

  const saveArticulos = async (event) => {
    event.preventDefault();
    // Guard síncrono: si ya hay un guardado en curso, ignorar el click/submit.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setValidaBoton(false);

    // Filtrar solo las cantidades mayores a 0
    const cantidadesValidas = cantidades.filter(item => item.cantidad > 0);

    if (cantidadesValidas.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No quantities",
        text: "Please enter at least one quantity greater than 0",
      });
      submittingRef.current = false;
      setValidaBoton(true);
      return;
    }

    // Calcular piezas por caja (suma de todas las cantidades)
    const piezasPorCaja = cantidadesValidas.reduce((sum, item) => sum + item.cantidad, 0);

    if (piezasPorCaja === 0) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "You must add the proportion of pieces per box!",
      });
      submittingRef.current = false;
      setValidaBoton(true);
      return;
    }

    // Obtener arrays de IDs
    const arrayColoresOfIds = arrayColores.map((c) => c._id);
    const arrayTallasOfIds = arrayTallas.map((talla) => talla._id);

    let totalProporcion = cantidadesValidas.length;
    const currentYear = new Date().getFullYear().toString();

    try {
      setLoading(true);

      const authHeaders = {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      };

      // Reutilizar el PreArticulo existente por código (evita duplicados);
      // si no existe, crearlo.
      let preArticuloId;
      const existente = await axios.get(
        `${URL_PRE_ARTICULOS}/codigo/${encodeURIComponent(nuevoCodigo)}`,
        authHeaders
      );

      if (existente.data && existente.data._id) {
        preArticuloId = existente.data._id;
      } else {
        const preArticuloResponse = await axios.post(
          URL_PRE_ARTICULOS,
          {
            codigo: nuevoCodigo,
            nombre: "NA",
            piezasPorCaja: piezasPorCaja,
            costoUSD: 0,
            costoMX: 0,
            venta: 0,
            utilidadP: 0,
            utilidadM: 0,
            lista1: 0,
            lista2: 0,
            lista3: 0,
            lista4: 0,
            observaciones: "Creado por China",
            lineas: "68535a2e49d00265b8a504b8",
            marcas: "68535a3949d00265b8a504bf",
            familias: "685ec927a42f1b003c555e76",
            colecciones: "68535a4949d00265b8a504cd",
            proveedores: user.email === 'admin@sistemify.com' ? '' : (userType === 'provider' ? user.id : ''),
            ano: currentYear,
            EAN: nuevoCodigo,
            RFID: generateEAN13(generateItemReference(nuevoCodigo, "", "")),
            precioEsp: "Si",
            colores: arrayColoresOfIds,
            tallas: arrayTallasOfIds,
            codigoSAT: "NA",
            codigoUnidad: "PZ",
          },
          authHeaders
        );
        preArticuloId = preArticuloResponse.data._id;
      }

      // Mapa local de RFIDs asignados: { colorId_tallaId: rfid }
      const rfidMap = {};

      // Crear proporciones para cada combinación con cantidad > 0
      for (const item of cantidadesValidas) {
        // Encontrar el color para obtener su prefijo
        const color = allColores.find(c => c._id === item.colorId);
        const talla = allTallas.find(t => t._id === item.tallaId);

        // Formar EAN legacy: nuevoCodigo + prefijo del color + nombre de talla
        const eanLegacy = `${nuevoCodigo}${color?.prefijo || ''}${talla?.name || ''}`;

        // Prepare proporcion data
        // No mandamos RFID — el backend asigna el RFID determinista (hash + probing)
        const proporcionData = {
          preArticulos: preArticuloId,
          cantidad: item.cantidad,
          colores: item.colorId,
          tallas: item.tallaId,
          EAN: eanLegacy,
        };

        try {
          const proporcionRes = await axios.post(
            `${URL_ARTICULOS_PROPORCION.replace(/\/$/, "")}China`,
            proporcionData,
            {
              headers: {
                Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
              },
            }
          );

          // Guardar el RFID secuencial en el mapa local (no en estado para evitar stale closure)
          const rfidAsignado = proporcionRes.data?.RFID;
          if (rfidAsignado) {
            rfidMap[`${item.colorId}_${item.tallaId}`] = rfidAsignado;
          }

          totalProporcion = totalProporcion - 1;

          if (totalProporcion === 0) {
            // Registrar en logs
            await axios.post(
              URL_LOGS,
              {
                tipo: "Crear Modelo China",
                detalle: `${nuevoCodigo} - ${piezasPorCaja} piezas`,
                user: user.id,
              },
              {
                headers: {
                  Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
                },
              }
            );

            Swal.fire("Good job!", "Created successfully", "success");

            submittingRef.current = false;
            setValidaBoton(true);
            setLoading(false);

            // Pasar rfidMap directamente para evitar stale closure en setTimeout
            setTimeout(() => {
              generarEtiquetasPDF(rfidMap);
            }, 500);
          }
        } catch (proporcionError) {
          console.log(proporcionError);
          submittingRef.current = false;
          setValidaBoton(true);
          setLoading(false);
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong creating proportions!",
            footer: proporcionError.response?.data?.message || proporcionError.response?.data?.error || proporcionError.message || "Ocurrió un error al crear las proporciones.",
          });
          return;
        }
      }
      
    } catch (error) {
      submittingRef.current = false;
      setValidaBoton(true);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
        footer: error.response?.data?.message || error.response?.data?.error || error.message || "Ocurrió un error inesperado.",
      });
      console.log(error);
    }
  };

  return (
    <>
      <Header />
      <br />
      <br />
      
      {(user?.menu_articulos || userType === 'provider') ? (
        <div className="card col-12">
          {/* <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuArticulos"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
           
          </Row> */}
          
          <h3 align="center">China Label Generator</h3>
          
          <Row>
            <Col md={4}>
              <Label className="mr-sm-2">Article Code</Label>
              <Input
                type="text"
                placeholder="Enter article code"
                value={nuevoCodigo}
                onChange={(e) => setNuevoCodigo(e.target.value)}
                className="mb-3"
              />
            </Col>
            <Col md={4}>
              <Label className="mr-sm-2">Select Colors</Label>
              <Autocomplete
                multiple
                size="small"
                value={selectedColores}
                onChange={(event, newValue) => {
                  setSelectedColores(newValue);
                }}
                options={allColores.map(color => color._id)}
                getOptionLabel={(option) => {
                  const color = allColores.find(c => c._id === option);
                  return color ? color.name : '';
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Colors"
                    variant="outlined"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const color = allColores.find(c => c._id === option);
                    return (
                      <span
                        key={option}
                        {...getTagProps({ index })}
                        style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '12px'
                        }}
                      >
                        {color?.name}
                      </span>
                    );
                  })
                }
              />
            </Col>
            <Col md={4}>
              <Label className="mr-sm-2">Select Sizes</Label>
              <Autocomplete
                multiple
                size="small"
                value={selectedTallas}
                onChange={(event, newValue) => {
                  setSelectedTallas(newValue);
                }}
                options={allTallas.map(talla => talla._id)}
                getOptionLabel={(option) => {
                  const talla = allTallas.find(t => t._id === option);
                  return talla ? talla.name : '';
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Sizes"
                    variant="outlined"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const talla = allTallas.find(t => t._id === option);
                    return (
                      <span
                        key={option}
                        {...getTagProps({ index })}
                        style={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '12px'
                        }}
                      >
                        {talla?.name}
                      </span>
                    );
                  })
                }
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={12} className="text-center">
              <Button
                color="primary"
                onClick={generarTabla}
                disabled={!nuevoCodigo.trim() || selectedColores.length === 0 || selectedTallas.length === 0}
              >
                Generate Table <i className="fas fa-table"></i>
              </Button>
            </Col>
          </Row>

          {nuevoCodigo && mostrarTabla && (
            <Row>
              <Col md={12}>
                <br />
                <div style={{
                  backgroundColor: '#d1ecf1',
                  border: '1px solid #bee5eb',
                  borderRadius: '0.25rem',
                  padding: '0.75rem 1.25rem',
                  marginBottom: '1rem'
                }}>
                  <h5>Article Configuration:</h5>
                  <p><strong>Code:</strong> {nuevoCodigo}</p>
                  <p><strong>Colors:</strong> {selectedColores.length} selected</p>
                  <p><strong>Sizes:</strong> {selectedTallas.length} selected</p>
                </div>
              </Col>
            </Row>
          )}

          {/* Tabla de cantidades */}
          {mostrarTabla && nuevoCodigo && (
            <>
              <Row>
                <Col md={8}>
                  <h4>Quantities by Color and Size</h4>
                </Col>
                <Col md={4}>
                  <ButtonGroup id="logoutBoton">
                    <Button
                      size="sm"
                      className="btn"
                      color="warning"
                      onClick={limpiarCantidades}
                    >
                      Clear <i className="fas fa-eraser"></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn"
                      color="info"
                      onClick={saveArticulos}
                      disabled={!validaBoton || loading || cantidades.every(item => item.cantidad === 0)}
                    >
                      {loading ? "Saving..." : "Save & Generate"} <i className="fas fa-save"></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn"
                      color="danger"
                      onClick={() => {
                        setMostrarTabla(false);
                        setNuevoCodigo("");
                        setSelectedColores([]);
                        setSelectedTallas([]);
                      }}
                    >
                      Close <i className="fas fa-times"></i>
                    </Button>
                  </ButtonGroup>
                </Col>
              </Row>


<div className="bg-gray-50 p-6 rounded-lg">
  <Label className="text-gray-700 font-medium mb-3 block">
    Label Proportion
  </Label>
  
  {/* Contenedor con scroll horizontal */}
  <div 
    style={{ 
      overflowX: 'auto',
      overflowY: 'visible',
      maxWidth: '100%',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      backgroundColor: 'white'
    }}
  >
    <Table 
      size="sm" 
      striped 
      borderless 
      className="mb-0"
      style={{ 
        minWidth: `${Math.max(600, 150 + (arrayTallas.length * 80))}px`,
        width: 'auto'
      }}
    >
      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}>
        <tr className="border-b border-gray-200">
          <th 
            className="py-3 text-left font-normal sticky-column" 
            style={{ 
              width: "150px",
              minWidth: "150px",
              position: 'sticky',
              left: 0,
              backgroundColor: '#f8f9fa',
              zIndex: 20,
              borderRight: '2px solid #dee2e6'
            }}
          >
            Color / Size
          </th>
          {arrayTallas.map((talla, index) => (
            <th
              key={talla._id}
              className="py-3 text-center font-medium text-gray-600"
              style={{
                width: "80px",
                minWidth: "80px",
                whiteSpace: 'nowrap'
              }}
            >
              {talla.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {arrayColores.map((color, colorIndex) => (
          <tr key={color._id} className="border-b border-gray-200">
            <td
              className="py-3 pr-4 text-gray-600 font-medium uppercase sticky-column"
              style={{
                width: "150px",
                minWidth: "150px",
                position: 'sticky',
                left: 0,
                backgroundColor: colorIndex % 2 === 0 ? '#f8f9fa' : 'white',
                zIndex: 10,
                borderRight: '2px solid #dee2e6'
              }}
            >
              {color.name}
            </td>
            {arrayTallas.map((talla) => {
              const item = cantidades.find(
                c => c.colorId === color._id && c.tallaId === talla._id
              );
              return (
                <td
                  key={talla._id}
                  className="py-2 text-center"
                  style={{
                    width: "80px",
                    minWidth: "80px",
                    padding: "8px 4px"
                  }}
                >
                  <Input
                    type="number"
                    min="0"
                    bsSize="sm"
                    value={item?.cantidad || 0}
                    onChange={(e) =>
                      handleCantidadChange(
                        color._id,
                        talla._id,
                        e.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      textAlign: "center",
                      fontSize: "12px",
                      padding: "4px 8px"
                    }}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>

  {/* Indicador de scroll si hay muchas columnas */}
  {arrayTallas.length > 8 && (
    <div className="text-center mt-2">
      <small className="text-muted">
        <i className="fas fa-arrows-alt-h"></i> Scroll horizontally to see all sizes
      </small>
    </div>
  )}
</div>

              <Row>
                <Col md={12}>
                  <br />
                  <div style={{ 
                    backgroundColor: '#d1ecf1', 
                    border: '1px solid #bee5eb', 
                    borderRadius: '0.25rem',
                    padding: '0.75rem 1.25rem',
                    textAlign: 'center'
                  }}>
                    <strong>Total labels to generate: </strong>
                    <span style={{ 
                      backgroundColor: '#007bff',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}>
                      {cantidades.reduce((sum, item) => sum + item.cantidad, 0)}
                    </span>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      ) : (
        <div className="container">
          <div style={{ 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb', 
            borderRadius: '0.25rem',
            padding: '0.75rem 1.25rem',
            textAlign: 'center'
          }}>
            <h4>Access Denied</h4>
            <p>You don't have permissions to access this section.</p>
          </div>
        </div>
      )}
      <br />
    </>
  );
}

export default GeneradorEtiquetasChina;